/**
 * Shared table utility functions for the SOHAndRUL feature.
 */
// Re-export the centralized computed-date formatter so existing imports
// (table cell renderers, CSV export, tests) keep a single source of truth.
export { formatComputedDate } from "../../../../Helper/DatePicker/DateConverter";

/**
 * Flatten an API row from { xLabel, metricsArray, metaInfo } format
 * into a flat object suitable for table rendering.
 */
export function flattenRow(item) {
  const row = { id: item.xLabel, batteryID: item.xLabel };
  (item.metricsArray || []).forEach(({ type, value }) => { row[type] = value; });
  Object.assign(row, item.metaInfo || {});
  return row;
}

/**
 * Narrow a schema's select/metaInfo list to what the current view actually needs.
 *
 * Keeps a schema field when its column is selected, or when it is named in extraFields —
 * fields the table needs regardless of column selection (renderer inputs, the active sort
 * column). Schema order is preserved and unknown names are dropped, so the result is always
 * a subset of what the org meta declares.
 *
 * @param {Array<string>} schemaFields      - org meta `select` or `metaInfo`
 * @param {Array<string>} selectedColumnKeys - dataIndex of every visible column
 * @param {Array<string>} extraFields        - always-request field names
 * @returns {Array<string>}
 */
export function narrowRequestFields(schemaFields, selectedColumnKeys, extraFields = []) {
  const wanted = new Set(
    [...(selectedColumnKeys || []), ...(extraFields || [])].filter(Boolean)
  );
  return (schemaFields || []).filter((field) => wanted.has(field));
}

/**
 * Identity of a battery-list table's current filter set, used to decide when pagination
 * and sort must reset back to page 1 / the default column.
 *
 * Compared by VALUE, not by reference: callers rebuild these objects as they re-render, so
 * a reference check would reset — and refetch — when nothing actually changed.
 *
 * @param {object} globalFilters
 * @param {Array}  additionalFilters
 * @param {string} defaultSortColumn
 * @returns {string}
 */
export function buildTableFiltersKey(globalFilters, additionalFilters, defaultSortColumn) {
  return JSON.stringify({
    globalFilters: globalFilters ?? {},
    additionalFilters: additionalFilters ?? [],
    defaultSortColumn: defaultSortColumn ?? null,
  });
}

/**
 * Build the battery list table title from the org's base-entity (singular) label.
 * "Bus" -> "Bus List", "Vehicle" -> "Vehicle List", "Battery" -> "Battery List".
 * Falls back to "Battery List" when the label is missing / blank.
 * @param {string} entityLabel - baseEntityTypeLable from useBaseEntityInfo()
 * @returns {string}
 */
export function buildBatteryListTitle(entityLabel) {
  const label = typeof entityLabel === "string" ? entityLabel.trim() : "";
  if (!label) return "Battery List";
  return `${label} List`;
}

/**
 * Backend field behind the "Odometer (km)" column on the SOH lists: the odometer captured
 * when SOH was last computed. Deliberately NOT `odometer`, which is the vehicle's current
 * reading (shown separately as "km Covered") — filtering that while displaying this one
 * returned rows whose odometer cell read "-".
 */
export const SOH_ODOMETER_FIELD = "latestSOHPredictionOdometer";

/**
 * Build the odometer range entry for the backend `filters` list, or null when no bound
 * is set. Uses the platform-wide range wire format shared with the generic table
 * filters: a list holding one JSON string of { start, end }. Either bound may be empty,
 * which the backend reads as open-ended.
 *
 * @param {{start: string, end: string}} odometer
 * @returns {{name: string, value: string[]}|null}
 */
export function buildOdometerRangeFilter(odometer) {
  const start = odometer?.start ?? "";
  const end = odometer?.end ?? "";
  if (start === "" && end === "") return null;
  return { name: SOH_ODOMETER_FIELD, value: [JSON.stringify({ start, end })] };
}

/**
 * The first org meta drill-down table node present under BatteryIntelligence, in the
 * order of `metaKeys` (e.g. ["SOHMigrationDrillDown", "SOHDrillDown"]), or undefined.
 * A key may be a dotted path to a nested node ("SOHMigrationDrillDown.pack" reads
 * SOHMigrationDrillDown.pack.table). A node counts as present when it is an object.
 *
 * @param {object|null|undefined} batteryIntelligenceMeta  metaInfo.BatteryIntelligence
 * @param {string[]} metaKeys
 * @returns {object|undefined}  the `<key>.table` node
 */
export function pickDrillDownTableSchema(batteryIntelligenceMeta, metaKeys) {
  const keys = Array.isArray(metaKeys) ? metaKeys : [];
  for (const key of keys) {
    const node = typeof key === "string"
      ? key.split(".").reduce((obj, part) => obj?.[part], batteryIntelligenceMeta)
      : undefined;
    const table = node?.table;
    if (table && typeof table === "object") return table;
  }
  return undefined;
}

/**
 * Drill-down table schema for SOHBreakdownDrillDownModal: the org meta
 * `SOHDrillDown.table` node when present, else the local fallback columns (with an
 * empty select / metaInfo, as before). An optional `schemaFilter` (e.g.
 * filterSohHistoryDrillDownSchema) then narrows it to what the caller's endpoint
 * supports. Without a filter the result is exactly the unfiltered schema.
 *
 * @param {object|null|undefined} tableSchema  org meta { columnsConfig, columnOrdering, select, metaInfo }
 * @param {object} fallbackColumnsConfig
 * @param {string[]} fallbackColumnOrder
 * @param {Function|null} schemaFilter         (schema) => schema
 * @returns {{ columnsConfig: object, columnOrdering: string[], select: string[], metaInfo: string[] }}
 */
export function resolveDrillDownSchema(tableSchema, fallbackColumnsConfig, fallbackColumnOrder, schemaFilter = null) {
  const schema = tableSchema
    ? {
      columnsConfig: tableSchema.columnsConfig || {},
      columnOrdering: tableSchema.columnOrdering || [],
      select: tableSchema.select || [],
      metaInfo: tableSchema.metaInfo || [],
    }
    : {
      columnsConfig: fallbackColumnsConfig || {},
      columnOrdering: fallbackColumnOrder || [],
      select: [],
      metaInfo: [],
    };
  return typeof schemaFilter === "function" ? schemaFilter(schema) : schema;
}

/**
 * Props for the drill-down table's list actions (CSV download, mail, row selection).
 * With hideListActions the download and mail buttons are hidden and row selection
 * (which only feeds those actions) is turned off -- for endpoints with no list-actions.
 *
 * @param {boolean} hideListActions
 * @param {{ getCSVData: Function, onSendEmail: Function, rowSelection: object }} handlers
 */
export function getDrillDownListActionProps(hideListActions, { getCSVData, onSendEmail, rowSelection } = {}) {
  if (hideListActions) {
    return { disableDownloads: true, showEmailComposer: false, rowSelection: undefined };
  }
  return { disableDownloads: false, getCSVData, onSendEmail, rowSelection };
}

/**
 * State-setter helper: returns `prev` when `next` holds the same items in the same
 * order, else `next`. Keeps an array's identity stable across rebuilds so effects
 * that depend on it do not rerun.
 */
export function keepIfSameItems(prev, next) {
  const a = Array.isArray(prev) ? prev : [];
  const b = Array.isArray(next) ? next : [];
  if (a.length === b.length && a.every((v, i) => v === b[i])) return prev;
  return b;
}

/**
 * Row adapter for the shared "operationalStatus" field indicator, whose preset reads
 * tag_battery_status (value) and tag_battery_sub_status (reason). The SOH list endpoints
 * return the status as `operationalStatus` (display name, e.g. "Non-Operational", which
 * the preset matches) and no sub-status, so only the value is copied: the tooltip then
 * shows without a reason.
 * @param {object|null|undefined} record
 * @returns {{ tag_battery_status: string|null }}
 */
export function toOperationalStatusIndicatorRecord(record) {
  return { tag_battery_status: record?.operationalStatus ?? null };
}

// Fields that hold the platform's internal battery id, and the renderer that shows it.
const INTERNAL_ID_DATA_INDEXES = ["batteryID", "xLabel"];
const INTERNAL_ID_RENDERER = "battery-id-link";
const DEVICE_ID_FIELD = "deviceID";

const isInternalIdColumn = (column) =>
  INTERNAL_ID_DATA_INDEXES.includes(column?.dataIndex) ||
  INTERNAL_ID_DATA_INDEXES.includes(column?.exportDataIndex) ||
  column?.valueRenderingType === INTERNAL_ID_RENDERER;

/**
 * The internal batteryID must never be shown, downloaded or emailed. An org meta
 * columnsConfig (keyed by column key) can still name it, so before the columns are
 * built:
 * - the first battery id column becomes the org-facing device id column (same map
 *   key, so columnOrdering still places it), labelled with the org's id label; the
 *   column's key becomes deviceID, which the backend can also sort by;
 * - it is dropped instead when the table already shows a device id column, and any
 *   further battery id columns are dropped.
 *
 * Pair with withDeviceIdField so the request returns the device id the column reads.
 *
 * @param {object} columnsConfig - org meta columnsConfig
 * @param {string} deviceIdLabel - globalConfig.baseEntity.idLabel or a fallback
 * @param {Array<string>} [columnOrder] - org meta columnOrdering; a device id column
 *   left out of it is not shown, so it does not count as present
 */
export function replaceInternalIdColumns(columnsConfig, deviceIdLabel, columnOrder) {
  if (!columnsConfig || typeof columnsConfig !== "object" || Array.isArray(columnsConfig)) {
    return columnsConfig;
  }
  const entries = Object.entries(columnsConfig);
  const isShown = (columnKey) =>
    !Array.isArray(columnOrder) || columnOrder.length === 0 || columnOrder.includes(columnKey);
  const hasDeviceIdColumn = entries.some(
    ([columnKey, column]) =>
      !isInternalIdColumn(column) && column?.dataIndex === DEVICE_ID_FIELD && isShown(columnKey)
  );
  let replaced = hasDeviceIdColumn;
  return entries.reduce((result, [columnKey, column]) => {
    if (!isInternalIdColumn(column)) {
      result[columnKey] = column;
      return result;
    }
    if (replaced) return result;
    replaced = true;
    const { exportDataIndex, defaultSortOrder, ...rest } = column;
    result[columnKey] = {
      ...rest,
      title: deviceIdLabel,
      headerLabel: deviceIdLabel,
      dataIndex: DEVICE_ID_FIELD,
      key: DEVICE_ID_FIELD,
      valueRenderingType: "digital-twin-link",
    };
    return result;
  }, {});
}

/**
 * A request metaInfo list that always asks for the device id: the id columns read
 * it, and the download / email identify rows by it, whether or not the org meta
 * metaInfo lists it.
 *
 * @param {Array<string>} metaInfo
 * @returns {Array<string>}
 */
export function withDeviceIdField(metaInfo) {
  const fields = Array.isArray(metaInfo) ? metaInfo : [];
  return fields.includes(DEVICE_ID_FIELD) ? fields : [...fields, DEVICE_ID_FIELD];
}
